import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  message,
  notification,
} from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiService, Control, Hazard } from "../../api";
import AddHazardModal from "./AddHazardModal";
import AddControlModal from "./AddControlModal";
import { NotificationType } from "../../constants";

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "select" | "text";
  record: Hazard;
  index: number;
  children: React.ReactNode;
  editable: boolean;
}

type Props = {
  schema: any;
  formProps: any;
  formId?: number;
  requestId?: number;
  readonly?: boolean;
  values?: any;
  onSuccess: (result: any) => void;
  duplicateRequestId?: number | undefined;
  setDuplicateRequestId?: (duplicateRequestId: number | undefined) => void;
};

type ExtendedHazard = Hazard & {
  controls?: Control[];
};

const AssessmentFormComponent = ({
  formId,
  requestId,
  readonly,
  onSuccess,
  duplicateRequestId,
  setDuplicateRequestId,
}: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [hazards, setHazards] = useState<ExtendedHazard[]>([]);
  const [form] = Form.useForm();
  const [isAddHazardModalVisible, setIsAddHazardModalVisible] =
    useState<boolean>(false);
  const [isAddControlModalVisible, setIsAddControlModalVisible] =
    useState<boolean>(false);
  const [selectedHazard, setSelectedHazard] = useState<number | undefined>(
    undefined
  );

  const EditableCell: React.FC<EditableCellProps> = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    const inputNode =
      inputType === "select" ? (
        <Select disabled={readonly}>
          <Select.Option value="Low">{t("Low")}</Select.Option>
          <Select.Option value="Medium">{t("Medium")}</Select.Option>
          <Select.Option value="High">{t("High")}</Select.Option>
        </Select>
      ) : (
        <Input />
      );

    const id = record && record.id;

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={[id, dataIndex]}
            style={{ margin: 0 }}
            rules={[
              {
                required: true,
                message: t("Please Input {title}!", { title }),
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const handleSetIsAddHazardModalVisible = (visible: boolean) => {
    setIsAddHazardModalVisible(visible);
  };

  const handleSetIsAddControlModalVisible = (visible: boolean) => {
    setIsAddControlModalVisible(visible);
  };

  useEffect(() => {
    (async () => {
      if (duplicateRequestId === undefined) {
        setLoading(true);

        getFormValues(requestId, formId);

        setLoading(false);
      }
    })();
  }, [form, formId, requestId]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      getFormValues(duplicateRequestId, formId);

      setLoading(false);
    })();
  }, [duplicateRequestId]);

  const handleAddHazard = (hazard: Hazard) => {
    const isInArray = hazards.some((x) => x.id === hazard.id);
    if (isInArray) {
      message.error(t("This hazard already exists"));
    } else {
      setHazards((prev) => [...prev, hazard]);
    }
  };

  const openNotificationWithIcon = (type: NotificationType) => {
    if (type === "success") {
      notification[type]({
        message: t("Success"),
        description: t("Form has been successfully saved."),
      });
    } else {
      notification[type]({
        message: t("Error"),
        description: t("Something went wrong."),
      });
    }
  };

  const handleAddControl = (hazardId: number, control: Control) => {
    const hazard = hazards.find((x) => x.id === hazardId);

    const isInArray =
      hazard?.controls && hazard?.controls.some((x) => x.id === control.id);

    if (isInArray) {
      message.error(t("This control already exists"));
    } else {
      setHazards((prevHazards) =>
        prevHazards.map((h) =>
          h.id === hazardId
            ? { ...h, controls: [...(h.controls || []), control] }
            : h
        )
      );
    }
  };

  const getFormValues = async (requestId?: number, form_id?: number) => {
    if (!requestId) return undefined;
    const result = await ApiService.apiMainRequestFormRetrieve(
      form_id!,
      requestId!
    );

    const initialValues: Record<string, any> = {};

    setHazards(result);

    result.map((x: any) => {
      initialValues[x.id] = {
        initial_risk: x.initial_risk,
        residual_risk: x.residual_risk,
      };
    });

    form.setFieldsValue(initialValues);
  };

  const expandedRowRender = (record: ExtendedHazard) => {
    return (
      <Table
        dataSource={record.controls}
        bordered
        pagination={false}
        rowKey="id"
      >
        <Table.Column title={t("Name")} dataIndex="name"></Table.Column>
        <Table.Column
          title={t("Description")}
          dataIndex="description"
        ></Table.Column>
        <Table.Column
          width="15%"
          render={(control: Control) => (
            <Space size="middle">
              <Button
                type="link"
                onClick={() => handleDeleteControl(record.id, control.id)}
              >
                {t("Delete control")}
              </Button>
            </Space>
          )}
        ></Table.Column>
      </Table>
    );
  };

  const handleSave = async (values: any) => {
    setSaveLoading(true);
    const dataToSend = hazards.map((x) => {
      return {
        hazard_id: x.id,
        initial_risk: values[x.id].initial_risk,
        residual_risk: values[x.id].residual_risk,
        controls: x.controls?.map((x) => x.id) || [],
      };
    });

    try {
      const result = await ApiService.apiMainRequestFormCreate({
        form_id: formId ?? 0,
        request_id: requestId ?? null,
        data: dataToSend,
      });

      openNotificationWithIcon("success");
      onSuccess(result);
      getFormValues(requestId, formId);
    } catch (e) {
      openNotificationWithIcon("error");
      console.log(e);
    }
    setSaveLoading(false);
  };

  const handleOpenControlModal = (hazardId: number) => {
    setSelectedHazard(hazardId);
    setIsAddControlModalVisible(true);
  };

  const handleDeleteHazard = (hazardId: number) => {
    setHazards((prevHazards) =>
      prevHazards.filter((hazard) => hazard.id !== hazardId)
    );
  };

  const handleDeleteControl = (hazardId: number, controlId: number) => {
    setHazards((prevHazards) =>
      prevHazards.map((hazard) =>
        hazard.id === hazardId
          ? {
              ...hazard,
              controls:
                hazard.controls?.filter(
                  (control) => control.id !== controlId
                ) || [],
            }
          : hazard
      )
    );
  };

  const columns = [
    {
      title: t("ID"),
      dataIndex: "id",
      editable: false,
    },
    {
      title: t("Name"),
      dataIndex: "name",
      editable: false,
    },
    {
      title: t("Description"),
      dataIndex: "description",
      editable: false,
    },

    {
      title: t("Initial Risk"),
      dataIndex: "initial_risk",
      editable: true,
    },
    {
      title: t("Residual Risk"),
      dataIndex: "residual_risk",
      editable: true,
    },
    {
      width: "15%",
      render: (record: Hazard) => (
        <>
          <Space size="middle">
            <Button
              type="link"
              onClick={() => handleOpenControlModal(record.id)}
            >
              {t("Add Control")}
            </Button>
          </Space>
          {" | "}
          <Space size="middle">
            <Button type="link" onClick={() => handleDeleteHazard(record.id)}>
              {t("Delete Hazard")}
            </Button>
          </Space>
        </>
      ),
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: Hazard) => ({
        record,
        inputType: col.dataIndex.includes("risk") ? "select" : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: col.editable,
      }),
    };
  });

  return (
    <div>
      <Form form={form} component={false} onFinish={handleSave}>
        <Table
          title={() => (
            <Button
              type="primary"
              onClick={() => setIsAddHazardModalVisible(true)}
            >
              {t("Add hazard")}
            </Button>
          )}
          bordered
          loading={loading}
          dataSource={hazards}
          expandable={{ expandedRowRender }}
          rowKey="id"
          columns={mergedColumns}
          rowClassName="editable-row"
          components={{
            body: {
              cell: EditableCell,
            },
          }}
        ></Table>
        {readonly !== true ? (
          <Row justify="end">
            <Col>
              <Button
                style={{ width: 100, marginTop: "1em" }}
                type="primary"
                loading={saveLoading}
                onClick={() => form.submit()}
              >
                {t("Save")}
              </Button>
            </Col>
          </Row>
        ) : null}
      </Form>
      <AddHazardModal
        visible={isAddHazardModalVisible}
        setVisible={handleSetIsAddHazardModalVisible}
        handleAddHazard={handleAddHazard}
      />
      <AddControlModal
        visible={isAddControlModalVisible}
        setVisible={handleSetIsAddControlModalVisible}
        handleAddControl={handleAddControl}
        hazardId={selectedHazard!}
      />
    </div>
  );
};

export default AssessmentFormComponent;
